/**
 * Host (TV) view for an active Camel Up game. Read-only.
 *
 * Wordmark + current-turn strip on top, the racetrack below, then a three-up
 * row of panels: the pyramid, standings/wallets, and the public bet board.
 * On `leg_end`, the coin-review panel takes over the main column.
 */

import { PLAYER_COLOR_HEX, type Room } from '@mdev/common'
import { BetsPanel } from './BetsPanel'
import { BoardStrip } from './BoardStrip'
import { CU, CU_HEADING } from '../theme'
import { LegReviewPanel } from './LegReviewPanel'
import { PyramidPanel } from './PyramidPanel'
import { Standings } from './Standings'
import { Coins, Wordmark } from './pieces'
import type { CamelUpSettings, CamelUpState } from '../module'

interface Props {
  room: Room<CamelUpSettings>
  state: CamelUpState
}

export function HostGameView(props: Props) {
  const { room, state } = props
  const activeUid = state.status === 'active' ? state.seatOrder[state.activeIndex] : undefined
  const active = activeUid ? state.players[activeUid] : null

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        background: CU.pageBg,
        backgroundBlendMode: 'overlay, normal, normal',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      <Wordmark
        size={38}
        trailing={
          <span style={{ fontFamily: CU_HEADING, fontSize: 18, color: CU.inkSoft, letterSpacing: '0.04em' }}>
            Room <strong style={{ color: CU.ink }}>{room.code}</strong> · Leg{' '}
            <strong style={{ color: CU.ink }}>{state.leg}</strong>
          </span>
        }
      />

      {active && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            borderRadius: 12,
            background: CU.surfaceSolid,
            border: `1px solid ${CU.border}`,
            borderLeft: `5px solid ${PLAYER_COLOR_HEX[active.color]}`,
            boxShadow: CU.shadowSoft,
            alignSelf: 'flex-start',
          }}
        >
          <span style={{ fontSize: 24 }}>{active.avatar}</span>
          <span style={{ fontFamily: CU_HEADING, fontSize: 20, color: CU.ink }}>
            <strong>{active.displayName}</strong>'s turn
          </span>
          <span style={{ marginLeft: 8 }}>
            <Coins amount={active.coins} />
          </span>
        </div>
      )}

      <BoardStrip state={state} />

      {state.status === 'leg_end' && <LegReviewPanel state={state} />}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 18,
          alignItems: 'flex-start',
        }}
      >
        <PyramidPanel state={state} />
        <Standings state={state} />
        <BetsPanel state={state} />
      </div>
    </div>
  )
}
